<template>
  <div>
    <dashboard-core-app-bar>
      <template v-slot:extraContent>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn text color @click="newCategoryDialog = true" v-on="on">
              +
              <v-icon color>mdi-folder</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("New category") }}</span>
        </v-tooltip>
        <v-dialog v-model="newCategoryDialog" max-width="50vw">
          <v-card>
            <v-card-title>
              <span class="headline">
                {{
                $t(renamingCategory ? $t("Rename category") : $t("New category"), {})
                }}...
              </span>
            </v-card-title>
            <v-card-text>
              <v-text-field autofocus solo flat v-model="categoryName" :label="$t('Category')" />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="closeCreateCategoryDialog">{{ $t("Cancel") }}</v-btn>
              <v-btn
                text
                color="primary"
                @click="saveCategory"
              >{{ $t(renamingCategory ? $t("Rename") : $t("Create")) }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="deleteCategoryDialog" v-if="categorySelected" max-width="40vw">
          <v-card>
            <v-card-title>
              <span class="headline">
                {{ $t("Delete category ", { name: categorySelected.name })
                }}{{ categorySelected.name }} ?
              </span>
            </v-card-title>
            <v-card-subtitle>
              <v-checkbox
                v-model="alsoProducts"
                :label="
                  $t('Also delete products that belong to ', {
                    name: categorySelected.name
                  })
                "
              ></v-checkbox>
            </v-card-subtitle>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="deleteCategoryDialog = false">{{ $t("Cancel") }}</v-btn>
              <v-btn
                text
                color="primary"
                @click="deleteCategory(categorySelected)"
              >{{ $t("Delete") }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn text color @click="newProduct">
              +
              <v-icon v-on="on">mdi-food-fork-drink</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("New product") }}</span>
        </v-tooltip>
        <v-spacer />
        <v-text-field
          :label="$t('Search products')"
          color="primary"
          hide-details
          v-model="search"
          class="mx-2"
          autofocus
          clearable
          prepend-inner-icon="mdi-magnify"
        ></v-text-field>
        <v-spacer />

        <v-btn outlined color @click="publishMenu" :disabled="publishLoading">
          <v-progress-circular indeterminate v-if="publishLoading" size="26" color="primary"></v-progress-circular>
          {{
          $t("Publish menu")
          }}
        </v-btn>
      </template>
    </dashboard-core-app-bar>
    <BodyContainer>
      <v-row>
        <v-col class="col-sm-4 col-xs-4 col-lg-3 col-md-4 col-xl-3">
          <BodyCard>
            <v-tabs color="primary" vertical v-model="tab">
              <draggable
                class="v-tabs__container"
                @update="menu_move"
                draggable=".item"
                handle=".handle"
              >
                <v-tab
                  v-for="(category, index) in menu"
                  :key="category.id"
                  @click="selectedCategory = category.id"
                  :class="{ item: index > 1 }"
                  exact
                >
                  <v-hover v-slot:default="{ hover }" open-delay="100">
                    <v-row no-gutters justify="space-between" align="center">
                      <v-col cols="1">
                        <v-icon dense class="handle">mdi-drag-vertical</v-icon>
                      </v-col>
                      <v-col cols="7" class="text-left">{{ category.name }}</v-col>
                      <v-col cols="3" class="text-right">
                        <div :class="{ 'on-hover': hover }" v-if="index > 1 && hover">
                          <v-btn exact icon @click="startCategoryEdit(category)">
                            <v-icon dense>mdi-pencil</v-icon>
                          </v-btn>
                          <v-btn icon exact @click="showDeleteCategoryDialog(category)">
                            <v-icon dense>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                        <div v-else>{{ category.products.length }}</div>
                      </v-col>
                    </v-row>
                  </v-hover>
                </v-tab>
              </draggable>
            </v-tabs>
          </BodyCard>
        </v-col>
        <v-col class="col-sm-8 col-xs-8 col-lg-5 col-md-8 col-xl-5">
          <BodyCard>
            <div class="overflow-auto">
              <v-tabs-items v-model="tab">
                <v-tab-item v-for="category in menu" :key="category.id" three-line>
                  <v-card flat>
                    <ProductList
                      :products="category.products"
                      @product-selected="changeSelectedProduct"
                    />
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </div>
          </BodyCard>
        </v-col>
        <v-col
          v-if="this.$vuetify.breakpoint.name=='lg'  || this.$vuetify.breakpoint.name=='xl' "
          class="col-sm-6 col-xs-6 col-lg-4 col-md-4 col-xl-4"
        >
          <ProductEditor :product="selectedProduct" v-if="!!selectedProduct" />
        </v-col>
        <v-dialog
          v-else
          v-model="selectedProductActive"
          class="col-sm-6 col-xs-6 col-lg-4 col-md-6 col-xl-4"
        >
          <ProductEditor :product="selectedProduct" v-if="!!selectedProduct" />
        </v-dialog>
      </v-row>
    </BodyContainer>
    <v-dialog v-model="newProductDialog" width="35vw">
      <ProductEditor
        v-if="newProductDialog"
        :product="emptyProduct"
        @closeDialog="newProductDialog = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import http from "@/service/http";
import Product from "@/model/product.js";
import Category from "@/model/category.js";
import draggable from "vuedraggable";
import { firestore } from "@/firebase/firestore"; // TODO: cambiar esto para usarlo en objetos

export default {
  components: {
    draggable,
    DashboardCoreAppBar: () =>
      import("@/views/dashboard/components/core/AppBar"),
    BodyContainer: () => import("@/views/dashboard/components/BodyContainer"),
    BodyCard: () => import("@/views/dashboard/components/BodyCard"),
    ProductEditor: () =>
      import("@/views/dashboard/components/products/ProductEditor"),
    ProductList: () =>
      import("@/views/dashboard/components/products/ProductList")
  },
  data() {
    return {
      publishLoading: false,
      tab: null,
      categoryName: "",
      alsoProducts: false,
      newCategoryDialog: false,
      categorySelected: null,
      deleteCategoryDialog: false,
      selectedProduct: null,
      selectedCategory: null,
      search: "",
      defaultImage: require("@/assets/no-image.png"),
      tabs: [],
      wait: false,
      newProductDialog: false,
      renamingCategory: false,
      selectedProductActive: false
    };
  },

  methods: {
    menu_move(value) {
      let menu = this.menu;
      this.order = [...Array(menu.length).keys()];
      // this.wait = true;
      if (value.newIndex >= menu.length) {
        var k = value.newIndex - menu.length + 1;
        while (k--) {
          this.order.push(undefined);
        }
      }
      this.order.splice(
        value.newIndex,
        0,
        this.order.splice(value.oldIndex, 1)[0]
      );

      let restaurantId = this.$store.getters.restaurant.id;
      let db = firestore;
      let batch = db.batch();

      this.order.forEach(function(oldIndex, newIndex) {
        if (menu[oldIndex].index >= 0 && menu[oldIndex].index != newIndex) {
          let catRef = db
            .collection(`restaurants/${restaurantId}/categories`)
            .doc(menu[oldIndex].id);
          batch.update(catRef, { index: newIndex });
        }
      });
      batch.commit();
      return false;
      // this.wait = false;
      // this.menu = this.$store.getters.menu;
    },

    async publishMenu() {
      this.publishLoading = true;
      await http.publishMenu();
      this.publishLoading = false;
    },

    saveCategory() {
      let category;
      if (!this.renamingCategory) {
        category = new Category({
          name: this.categoryName,
          index: this.menu.length + 1
        });
      } else {
        category = this.$store.getters.categories.find(
          category => this.selectedCategory === category.id
        );
        category.name = this.categoryName;
      }
      category.save();
      this.categoryName = "";
      this.newCategoryDialog = false;
      this.renamingCategory = false;
    },

    deleteCategory() {
      if (this.alsoProducts) {
        // TODO Delete products too
        this.menu.forEach(category => {
          if (category.id == this.categorySelected.id) {
            category.products.forEach(product => {
              product.delete();
            });
          }
        });
      }
      new Category({ id: this.categorySelected.id }).delete();
      this.categorySelected = null;
    },

    changeSelectedProduct(product) {
      if (product != null) {
        this.selectedProductActive = true;
        this.selectedProduct = product.clone();
      } else {
        this.selectedProduct = null;
      }
    },

    newProduct() {
      this.newProductDialog = true;
    },

    showDeleteCategoryDialog(category) {
      this.deleteCategoryDialog = true;
      this.categorySelected = category;
    },

    closeCreateCategoryDialog() {
      this.newCategoryDialog = false;
      this.categoryName = "";
    },

    startCategoryEdit(category) {
      this.newCategoryDialog = true;
      this.categoryName = category.name;
      this.renamingCategory = true;
    },

    slugify(str) {
      var map = {
        "-": " ",
        a: "á|à|ã|â|À|Á|Ã|Â",
        e: "é|è|ê|É|È|Ê",
        i: "í|ì|î|Í|Ì|Î",
        o: "ó|ò|ô|õ|Ó|Ò|Ô|Õ",
        u: "ú|ù|û|ü|Ú|Ù|Û|Ü",
        c: "ç|Ç",
        n: "ñ|Ñ"
      };

      str = str.toLowerCase();

      for (var pattern in map) {
        str = str.replace(new RegExp(map[pattern], "g"), pattern);
      }

      return str;
    }
  },
  computed: {
    emptyProduct() {
      return new Product({ category: this.selectedCategory });
    },

    menu() {
      let search = this.search;
      let menuCopy = JSON.parse(JSON.stringify(this.$store.getters.menu));
      menuCopy.map(function(category) {
        category.products = category.products.map(product =>
          Product._fromMap(product)
        );
        return category;
      });

      if (search != "") {
        menuCopy.forEach(category => {
          category.products = category.products.filter(product => {
            let isDescriptionMatch = product.description
              ? this.slugify(product.description.toLowerCase()).includes(
                  this.slugify(search.toLowerCase())
                )
              : false;
            return (
              this.slugify(product.name.toLowerCase()).includes(
                this.slugify(search.toLowerCase())
              ) || isDescriptionMatch
            );
          });
        });
        return menuCopy;
      }
      return this.$store.getters.menu;
    }
  }

  //
};
</script>

<style>
/* This is for documentation purposes and will not be needed in your application */
#create .v-speed-dial {
  position: absolute;
}

#create .v-btn--floating {
  position: relative;
}
</style>
